/**
 * How a screen describes a model the catalog no longer offers (#2014).
 *
 * An agent can still be assigned a model that has since retired — nothing is
 * deleted, so it keeps resolving and keeps running. What changes is that the
 * operator can no longer pick it, and the pickers previously said only
 * "not in the model catalog", which is true of a typo too and explains
 * nothing.
 *
 * One helper rather than the string inlined at each picker, because three
 * surfaces show it — the agent assignment page, the plan editor and the org
 * override — and three copies of a sentence is three chances for them to drift
 * into saying different things about the same model.
 */
import type { RetiredModelSummary } from '@/lib/api'

/** Index the retired list for lookup by model spec. */
export function retiredByPattern(
  retired: readonly RetiredModelSummary[],
): ReadonlyMap<string, RetiredModelSummary> {
  return new Map(retired.map(r => [r.namePattern, r]))
}

/**
 * The parenthetical a picker appends to a current selection that is no longer
 * offered.
 *
 * Returns the specific reason when we know it, and the existing generic
 * wording when we do not — a model that was never in the catalog at all is a
 * different situation and must not be described as retired.
 */
export function retiredOptionSuffix(retired: RetiredModelSummary | undefined): string {
  if (!retired) return 'not in the model catalog'
  return retired.reason === 'noFixedPrice'
    // Deliberately concrete about the consequence. This is the case where the
    // model still runs but its cost cannot be counted, which is the reason an
    // operator should care enough to change it.
    ? 'no longer priced — this model routes to whichever provider it picks, so its cost cannot be tracked'
    : 'no longer offered by OpenRouter'
}
